import { MetaGrowthDashboardDto } from "@/features/meta-growth/api/meta-growth.api";
import { formatters } from "@/features/orders-analytics/utils/formatters";

// Panel A is the all-time descriptive cohort worth ("audience worth" / "dormant value
// at risk") — NOT value the Meta campaign generated. Panel B is window-scoped ad spend
// and results. Different time bases, kept visually distinct via each panel's `basis`.
const PANEL_A_BASIS = "All-time audience worth (descriptive — not campaign-attributed)";
const PANEL_B_BASIS = "Windowed ad spend & results";

const EMPTY = "—";

export type CohortRowVM = {
    audienceSpecId: string;
    label: string;
    memberCount: number;
    value: string;
};

export type CampaignRowVM = {
    objectId: string;
    objectName: string;
    spend: string;
    // Pre-formatted: a conversion count that was not derivable renders as the
    // empty-state dash, never as a fabricated 0.
    results: string;
    costPerResult: string;
};

export type RecommendationVM = {
    code: string;
    severity: "info" | "warning";
    message: string;
};

export type PanelAVM = {
    basis: string;
    totalValue: string;
    rows: CohortRowVM[];
    // True when a cohort hit the page cap: its worth is a partial undercount, so the UI
    // must warn rather than present it as complete.
    truncated: boolean;
    // Cohorts whose read FAILED: absent from the rows and from the total, which is
    // therefore incomplete by an unknown amount. Kept separate from `truncated`
    // because the operator's response differs — a capped cohort is as complete as it
    // gets, a failed one is worth retrying.
    degraded: string[];
};

// Advisory codes derived from the value/ROAS side. When the value section is absent
// they cannot be trusted (their cause is the missing value, not what they claim), so
// the view model drops them defensively.
const VALUE_DERIVED_CODES: string[] = [
    "low_roas",
    "strong_roas",
    "roas_unavailable_currency",
    "roas_unavailable_mixed_currency",
];

export type PanelBVM = {
    basis: string;
    totalSpend: string;
    rows: CampaignRowVM[];
};

export type MetaGrowthVM = {
    blendedRoas: string;
    windowedValue: string;
    panelA: PanelAVM;
    panelB: PanelBVM;
    recommendations: RecommendationVM[];
};

const EMPTY_VM: MetaGrowthVM = {
    blendedRoas: EMPTY,
    windowedValue: EMPTY,
    panelA: { basis: PANEL_A_BASIS, totalValue: EMPTY, rows: [], truncated: false, degraded: [] },
    panelB: { basis: PANEL_B_BASIS, totalSpend: EMPTY, rows: [] },
    recommendations: [],
};

// Never fabricate a number: a non-finite value or an unknown currency renders as the
// empty-state dash, never as 0 / NaN.
const formatAmount = (value: number | null, currency: string | null): string =>
    currency !== null && typeof value === "number" && Number.isFinite(value)
        ? `${formatters.formatNumber(value)} ${currency}`
        : EMPTY;

// ROAS is only meaningful when the value side is present (cohorts !== null) and the
// backend produced a finite ratio. A degraded value section reports "—", never 0.
// A count is only ever shown when it was actually derived; null means "unknown".
const formatCount = (value: number | null): string =>
    typeof value === "number" && Number.isFinite(value) ? formatters.formatNumber(value) : EMPTY;

const formatRoas = (roas: number | null, valuePresent: boolean): string =>
    valuePresent && typeof roas === "number" && Number.isFinite(roas) ? roas.toFixed(2) : EMPTY;

const fromData = (dto: MetaGrowthDashboardDto | null): MetaGrowthVM => {
    if (dto === null) {
        return EMPTY_VM;
    }

    const { attribution, campaigns, cohorts } = dto;
    const valuePresent = cohorts !== null;

    const panelA: PanelAVM =
        cohorts !== null
            ? {
                  basis: PANEL_A_BASIS,
                  totalValue: formatAmount(cohorts.totals.value, attribution.valueCurrency),
                  rows: cohorts.rows.map((row) => ({
                      audienceSpecId: row.audienceSpecId,
                      label: row.label,
                      memberCount: row.memberCount,
                      value: formatAmount(row.value, attribution.valueCurrency),
                  })),
                  truncated: cohorts.truncated,
                  // Defensive read: a backend that predates the degraded signal sends
                  // nothing here, which must read as "no signal", never as a crash.
                  degraded: Array.isArray(cohorts.degraded) ? cohorts.degraded : [],
              }
            : { basis: PANEL_A_BASIS, totalValue: EMPTY, rows: [], truncated: false, degraded: [] };

    const panelB: PanelBVM =
        campaigns !== null
            ? {
                  basis: PANEL_B_BASIS,
                  totalSpend: formatAmount(attribution.totalSpend, attribution.spendCurrency),
                  rows: campaigns.rows.map((row) => ({
                      objectId: row.objectId,
                      objectName: row.objectName,
                      spend: formatAmount(row.spend, row.currency),
                      results: formatCount(row.results),
                      costPerResult: formatAmount(row.costPerResult, row.currency),
                  })),
              }
            : { basis: PANEL_B_BASIS, totalSpend: EMPTY, rows: [] };

    return {
        blendedRoas: formatRoas(attribution.blendedRoas, valuePresent),
        windowedValue: valuePresent
            ? formatAmount(attribution.totalValue, attribution.valueCurrency)
            : EMPTY,
        panelA,
        panelB,
        recommendations: dto.recommendations
            .filter((recommendation) => valuePresent || !VALUE_DERIVED_CODES.includes(recommendation.code))
            .map((recommendation) => ({
                code: recommendation.code,
                severity: recommendation.severity,
                message: recommendation.message,
            })),
    };
};

export const metaGrowthViewModel = { fromData };
